import React, { useState, useEffect, useRef } from "react";
import {
  IS_CONFIGURED, supabase,
  sbSignIn, sbSignUp, sbSignOut,
  sbGetProfile, sbCreateProfile,
  sbGetProperties, sbGetTickets, sbCreateTicket, sbUpdateTicket,
  sbGetContractors, sbCreateContractor,
  sbGetPayments, sbCreatePayment, sbGetAllProfiles,
  sbGetOwners, sbCreateOwner, sbGetInvoices, sbCreateInvoice, sbUpdateInvoice,
  sbCreateProperty, sbUpdateProperty,
  sbInviteRenter,
  sbGetLeases, sbCreateLease, sbUpdateLease,
  sbGetDocuments, sbCreateDocument, sbDeleteDocument,
  sbGetNotices, sbCreateNotice, sbUpdateNotice,
  sbGetComments, sbAddComment,
} from "./utils/supabase";

import LandingPage      from "./components/LandingPage";
import AuthPage         from "./components/AuthPage";
import ForgotPassword   from "./components/ForgotPassword";
import ResetPassword    from "./components/ResetPassword";
import RenterPortal     from "./components/RenterPortal";
import ManagerDashboard from "./components/ManagerDashboard";
import RentCalculator   from "./components/RentCalculator";
import { setLoggedInUser, clearLoggedInUser, logError } from "./utils/errorLogger";
import { sendResolutionEmail } from "./utils/emailService";

const EMPTY_SIGNUP = { name:"", email:"", password:"", confirm:"", regCode:"", unit:"", companyName:"" };
const EMPTY_LOGIN  = { email:"", password:"" };

// Suppress Supabase Web Locks noise from console
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (e) => {
    const msg = e?.reason?.message || "";
    if (msg.includes("AbortError") || msg.includes("Lock broken") || msg.includes("steal")) {
      e.preventDefault();
    }
  });
}

// Safe fetch helper -- returns fallback instead of throwing
const safe = async (fn, fallback = []) => {
  try { return await fn(); } catch { return fallback; }
};

const normalizeProfile = (p) => p ? ({
  id:          p.id,
  role:        p.role,
  name:        p.name,
  email:       p.email,
  propertyId:  p.property_id,
  unit:        p.unit,
  companyId:   p.company_id || 1,
  invitedAt:   p.invited_at || null,
  lastLoginAt: p.last_login_at || null,
}) : null;

export default function App() {
  // Detect public routes from URL on initial load
  const getInitialView = () => {
    const path = window.location.pathname.toLowerCase();
    if (path === "/calculator" || path === "/calculator/") return "calculator";
    return "landing";
  };

  const [view,        setView]        = useState(getInitialView);
  const [authMode,    setAuthMode]    = useState("login");
  const [authRole,    setAuthRole]    = useState("renter");
  const [currentUser, setCurrentUser] = useState(null);
  const [authError,   setAuthError]   = useState("");
  const [loading,     setLoading]     = useState(false);
  const isRecoveryFlow = useRef(false);

  // -- Data state -- start empty, filled from Supabase after login ----------
  const [users,       setUsers]       = useState([]);
  const [tickets,     setTickets]     = useState([]);
  const [properties,  setProperties]  = useState([]);
  const [contractors, setContractors] = useState([]);
  const [payments,    setPayments]    = useState([]);
  const [owners,      setOwners]      = useState([]);
  const [invoices,    setInvoices]    = useState([]);
  const [leases,      setLeases]      = useState([]);
  const [documents,   setDocuments]   = useState([]);
  const [notices,     setNotices]     = useState([]);

  const [loginForm,   setLoginForm]   = useState(EMPTY_LOGIN);
  const [signupForm,  setSignupForm]  = useState(EMPTY_SIGNUP);

  // -- Load data based on role (non-blocking) --------------------------------
  const loadAllData = async (role) => {
    if (!IS_CONFIGURED) return;

    // All roles need properties + tickets
    const [props, tix] = await Promise.all([
      safe(sbGetProperties),
      safe(sbGetTickets),
    ]);
    if (props.length) setProperties(props);
    if (tix.length)   setTickets(tix);

    // Renters also need their documents
    if (role === "renter") {
      const docs = await safe(sbGetDocuments);
      setDocuments(docs);
    }

    // Manager gets everything else
    if (role === "manager") {
      const [contrs, pays, profiles, owns, invs, lses, docs, nots] = await Promise.all([
        safe(sbGetContractors),
        safe(sbGetPayments),
        safe(sbGetAllProfiles),
        safe(sbGetOwners),
        safe(sbGetInvoices),
        safe(sbGetLeases),
        safe(sbGetDocuments),
        safe(sbGetNotices),
      ]);
      if (contrs.length)  setContractors(contrs);
      setPayments(pays);
      if (profiles.length) setUsers(profiles.map(normalizeProfile));
      setOwners(owns);
      setInvoices(invs);
      setLeases(lses);
      setDocuments(docs);
      setNotices(nots);
    }
  };

  // -- Session restore on mount ---------------------------------------------
  useEffect(() => {
    if (!IS_CONFIGURED) return;
    let mounted = true;

    (async () => {
      // Don't redirect away from public pages like /calculator
      const publicViews = ["calculator"];
      const isPublicPage = publicViews.includes(view);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted || !session?.user) return;
        if (isPublicPage) return; // stay on public page even if logged in

        setLoading(true);
        const profile = await safe(() => sbGetProfile(session.user.id), null);
        if (!mounted) return;

        if (profile) {
          const norm = normalizeProfile(profile);
          setCurrentUser(norm);
          setLoggedInUser(norm);
          setView(norm.role === "manager" ? "dashboard" : "renter");
          setLoading(false);
          loadAllData(norm.role); // background
        } else {
          setLoading(false);
        }
      } catch {
        if (mounted) setLoading(false);
      }
    })();

    // Only listen for logout + password recovery
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY") {
        isRecoveryFlow.current = true;
        setView("reset-password");
        setLoading(false);
      }
      if (event === "SIGNED_IN" && session?.user) {
        // Record last login for ALL sign in types including magic links
        supabase.from("profiles")
          .update({ last_login_at: new Date().toISOString() })
          .eq("id", session.user.id)
          .then(() => {});
      }
      if (event === "SIGNED_OUT") {
        setCurrentUser(null);
        setView("landing");
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  // -- Invite renter --------------------------------------------------------
  const handleInviteRenter = async (renter) => {
    try {
      const user = await sbInviteRenter(renter);
      // Refresh profiles list
      const profiles = await safe(sbGetAllProfiles);
      if (profiles.length) setUsers(profiles.map(normalizeProfile));
      return { success: true, user };
    } catch (err) {
      console.error("Invite renter failed:", err);
      return { success: false, error: err.message };
    }
  };

  // -- Ticket comment ops ---------------------------------------------------
  const handleGetComments = async (ticketId) => {
    try { return await sbGetComments(ticketId); }
    catch (err) { console.error("Get comments failed:", err); return []; }
  };

  const handleAddComment = async (ticketId, message, authorName) => {
    try {
      const saved = await sbAddComment(ticketId, message, authorName, 'manager');
      return saved;
    } catch (err) { console.error("Add comment failed:", err); }
  };

  // -- Notice ops -----------------------------------------------------------
  const handleAddNotice = async (notice) => {
    try {
      const saved = await sbCreateNotice(notice);
      setNotices((prev) => [saved, ...prev]);
      return saved;
    } catch (err) { console.error("Create notice failed:", err); }
  };

  const handleUpdateNotice = async (id, updates) => {
    try {
      const saved = await sbUpdateNotice(id, updates);
      setNotices((prev) => prev.map((n) => n.id === id ? saved : n));
    } catch (err) { console.error("Update notice failed:", err); }
  };

  // -- Property ops ---------------------------------------------------------
  const handleAddProperty = async (property) => {
    try {
      const saved = await sbCreateProperty(property);
      setProperties((prev) => [...prev, saved]);
    } catch (err) { console.error("Create property failed:", err); }
  };

  const handleUpdateProperty = async (id, updates) => {
    try {
      const saved = await sbUpdateProperty(id, updates);
      setProperties((prev) => prev.map((p) => p.id === id ? saved : p));
    } catch (err) { console.error("Update property failed:", err); }
  };

  // -- Helpers ---------------------------------------------------------------
  const goAuth = (mode, role) => {
    setAuthMode(mode); setAuthRole(role);
    setAuthError(""); setView("auth");
  };

  const signOut = async () => {
    if (IS_CONFIGURED) {
      try { await sbSignOut(); } catch { /* ignore 403 on expired sessions */ }
    }
    clearLoggedInUser();
    setCurrentUser(null);
    setUsers([]); setTickets([]); setProperties([]);
    setContractors([]); setPayments([]); setOwners([]);
    setInvoices([]); setLeases([]); setDocuments([]); setNotices([]);
    setView("landing");
    setLoginForm(EMPTY_LOGIN);
    setSignupForm(EMPTY_SIGNUP);
    setAuthError("");
  };

  // -- Login -----------------------------------------------------------------
  const handleLogin = async () => {
    setAuthError("");
    if (!IS_CONFIGURED) {
      setAuthError("Supabase not configured.");
      return;
    }
    try {
      const { user } = await sbSignIn(loginForm.email, loginForm.password);
      let profile = await safe(() => sbGetProfile(user.id), null);

      if (!profile) {
        profile = await sbCreateProfile({
          id: user.id, role: authRole,
          name: user.email.split("@")[0],
          email: user.email, company_id: 1,
        });
      }

      const norm = normalizeProfile(profile);
      setCurrentUser(norm);
      setLoggedInUser(norm);
      setView(norm.role === "manager" ? "dashboard" : "renter");
      loadAllData(norm.role); // background -- no await
    } catch (err) {
      logError(err, { context: "login" });
      setAuthError(
        err.message === "Invalid login credentials"
          ? "Incorrect email or password."
          : err.message
      );
    }
  };

  // -- Signup ----------------------------------------------------------------
  const handleSignup = async () => {
    setAuthError("");
    const { name, email, password, confirm, regCode, unit } = signupForm;

    if (!name || !email || !password)  { setAuthError("Please fill in all required fields."); return; }
    if (password !== confirm)           { setAuthError("Passwords do not match."); return; }
    if (password.length < 6)            { setAuthError("Password must be at least 6 characters."); return; }

    let propertyId = null;
    if (authRole === "renter") {
      if (!regCode) { setAuthError("Please enter your property registration code."); return; }
      if (!unit)    { setAuthError("Please enter your unit number."); return; }
      // Load properties if empty (renter signing up before data loaded)
      let props = properties;
      if (!props.length) props = await safe(sbGetProperties);
      const matched = props.find((p) => p.regCode?.toUpperCase() === regCode.trim().toUpperCase());
      if (!matched) { setAuthError("Invalid registration code. Please check with your property manager."); return; }
      propertyId = matched.id;
    }

    try {
      const { user, session: signUpSession } = await sbSignUp(email, password, {
        name, role: authRole, propertyId: propertyId || null, unit: unit || null,
      });

      if (!signUpSession) {
        setView("confirm-email");
        return;
      }

      await new Promise((r) => setTimeout(r, 600));
      const profile = await safe(() => sbGetProfile(user.id), null);
      const effectiveProfile = profile || {
        id: user.id, role: authRole, name, email,
        property_id: propertyId, unit: unit || null, company_id: 1,
      };

      const norm = normalizeProfile(effectiveProfile);
      setCurrentUser(norm);
      setLoggedInUser(norm);
      setView(authRole === "manager" ? "dashboard" : "renter");
      loadAllData(authRole); // background
    } catch (err) {
      setAuthError(err.message);
    }
  };

  // -- Ticket ops ------------------------------------------------------------
  const handleAddTicket = async (ticket) => {
    try {
      const saved = await sbCreateTicket(ticket);
      setTickets((prev) => [saved, ...prev]);
    } catch { setTickets((prev) => [ticket, ...prev]); }
  };

  const handleUpdateStatus = async (id, status) => {
    setTickets((prev) => prev.map((t) => t.id === id ? { ...t, status } : t));
    try {
      await sbUpdateTicket(id, { status });
      if (status === "RESOLVED") {
        const ticket   = tickets.find((t) => String(t.id) === String(id));
        const renter   = users.find((u) =>
          (ticket?.renterId && String(u.id) === String(ticket.renterId)) ||
          (ticket?.renter   && u.name?.toLowerCase() === ticket.renter?.toLowerCase())
        );
        const property = properties.find((p) => String(p.id) === String(ticket?.propertyId));
        console.log("Resolution email debug:", { ticketId: id, ticket, renter, renterEmail: renter?.email });
        if (ticket && renter?.email) {
          sendResolutionEmail(ticket, renter.email, property?.address || property?.name);
        } else {
          console.warn("Resolution email skipped — renter or email not found", { ticket, renter, usersCount: users.length });
        }
      }
    } catch (err) { console.error(err); }
  };

  const handleUpdateTicket = async (id, updates) => {
    setTickets((prev) => prev.map((t) => t.id === id ? { ...t, ...updates } : t));
    try { await sbUpdateTicket(id, updates); } catch (err) { console.error(err); }
  };

  // -- Contractor ops --------------------------------------------------------
  const handleAddContractor = async (contractor) => {
    try {
      const saved = await sbCreateContractor(contractor);
      setContractors((prev) => [...prev, saved]);
    } catch { setContractors((prev) => [...prev, contractor]); }
  };

  // -- Payment ops -----------------------------------------------------------
  const handlePaymentCreated = async (payment) => {
    try {
      const saved = await sbCreatePayment(payment);
      setPayments((prev) => [saved, ...prev]);
    } catch (err) {
      console.error("Create payment failed:", err);
      setPayments((prev) => [{ ...payment, id: Date.now() }, ...prev]);
    }
  };

  const handleAddRentPayment = (payment) =>
    handlePaymentCreated({ ...payment, type: "rent_received" });

  const handleRefreshPayments = async () => {
    try {
      const pays = await sbGetPayments();
      setPayments(pays);
    } catch (err) {
      console.error("Refresh payments failed:", err);
    }
  };

  // -- Lease ops -------------------------------------------------------------
  const handleAddLease = async (lease) => {
    try { const s = await sbCreateLease(lease); setLeases((p) => [...p, s]); }
    catch (err) { console.error("Create lease failed:", err); }
  };

  const handleUpdateLease = async (id, updates) => {
    try { const s = await sbUpdateLease(id, updates); setLeases((p) => p.map((l) => l.id === id ? s : l)); }
    catch (err) { console.error("Update lease failed:", err); }
  };

  // -- Document ops ----------------------------------------------------------
  const handleAddDocument = async (doc) => {
    try { const s = await sbCreateDocument(doc); setDocuments((p) => [s, ...p]); }
    catch (err) { console.error("Create document failed:", err); }
  };

  const handleDeleteDocument = async (id) => {
    setDocuments((p) => p.filter((d) => d.id !== id)); // optimistic
    try { await sbDeleteDocument(id); }
    catch (err) { console.error("Delete document failed:", err); }
  };

  // -- Owner ops -------------------------------------------------------------
  const handleAddOwner = async (owner) => {
    try { const s = await sbCreateOwner(owner); setOwners((p) => [s, ...p]); }
    catch (err) { console.error("Create owner failed:", err); }
  };

  // -- Invoice ops -----------------------------------------------------------
  const handleAddInvoice = async (invoice) => {
    try { const s = await sbCreateInvoice(invoice); setInvoices((p) => [s, ...p]); return s; }
    catch (err) { console.error("Create invoice failed:", err); }
  };

  const handleUpdateInvoice = async (id, updates) => {
    try { const s = await sbUpdateInvoice(id, updates); setInvoices((p) => p.map((i) => i.id === id ? s : i)); }
    catch (err) { console.error("Update invoice failed:", err); }
  };

  // -- Loading screen --------------------------------------------------------
  if (loading) return (
    <div style={{ minHeight:"100vh", background:"#0C0C0F", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <div style={{ fontSize:24, fontWeight:800, color:"#F0EDE8" }}>prop<span style={{color:"#E8622A"}}>desk</span></div>
      <div style={{ width:32, height:32, border:"3px solid #ffffff15", borderTop:"3px solid #E8622A", borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
      <div style={{ fontSize:13, color:"#9993A8" }}>Loading your workspace...</div>
      <span style={{ fontSize:11, color:"#E8622A", cursor:"pointer", textDecoration:"underline", marginTop:4 }} onClick={() => setLoading(false)}>
        Taking too long? Click to continue
      </span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // -- Routing ---------------------------------------------------------------

  // Public pages (no auth required)
  if (view === "calculator") return (
    <RentCalculator onBack={() => { window.history.replaceState(null, "", "/"); setView("landing"); }} />
  );

  if (view === "landing") return (
    <LandingPage
      onManagerLogin ={() => goAuth("login",  "manager")}
      onRenterLogin  ={() => goAuth("login",  "renter")}
      onRenterSignup ={() => goAuth("signup", "renter")}
    />
  );

  if (view === "reset-password")   return <ResetPassword onDone={() => setView("auth")} />;
  if (view === "forgot-password")  return <ForgotPassword onBack={() => setView("auth")} />;
  if (view === "confirm-email")    return (
    <div style={{ minHeight:"100vh", background:"#0C0C0F", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, padding:24 }}>
      <div style={{ fontSize:24, fontWeight:800, color:"#F0EDE8" }}>prop<span style={{color:"#E8622A"}}>desk</span></div>
      <div style={{ fontSize:18, fontWeight:700, color:"#F0EDE8", marginTop:8 }}>Check your email</div>
      <div style={{ fontSize:14, color:"#9993A8", textAlign:"center", maxWidth:340 }}>
        We sent a confirmation link to your email. Click it to activate your account then come back to sign in.
      </div>
      <button style={{ marginTop:8, background:"none", border:"none", color:"#E8622A", cursor:"pointer", fontSize:14 }} onClick={() => setView("auth")}>
        Back to Sign In
      </button>
    </div>
  );

  if (view === "auth") return (
    <AuthPage
      authMode={authMode}   setAuthMode={setAuthMode}
      authRole={authRole}   setAuthRole={setAuthRole}
      authError={authError}
      loginForm={loginForm}   setLoginForm={setLoginForm}
      signupForm={signupForm} setSignupForm={setSignupForm}
      properties={properties}
      onLogin={handleLogin}
      onSignup={handleSignup}
      onForgotPassword={() => setView("forgot-password")}
      onBack={() => { setAuthError(""); setView("landing"); }}
    />
  );

  if (view === "renter") return (
    <RenterPortal
      currentUser={currentUser}
      properties={properties}
      tickets={tickets}
      payments={payments}
      documents={documents}
      onAddTicket={handleAddTicket}
      onSignOut={signOut}
      onGetComments={handleGetComments}
      onRefreshPayments={handleRefreshPayments}
    />
  );

  if (view === "dashboard") return (
    <ManagerDashboard
      currentUser={currentUser}
      properties={properties}
      tickets={tickets}
      users={users}
      contractors={contractors}
      payments={payments}
      owners={owners}
      invoices={invoices}
      leases={leases}
      documents={documents}
      onInviteRenter={handleInviteRenter}
      onGetComments={handleGetComments}
      onAddComment={handleAddComment}
      notices={notices}
      onAddNotice={handleAddNotice}
      onUpdateNotice={handleUpdateNotice}
      onAddProperty={handleAddProperty}
      onUpdateProperty={handleUpdateProperty}
      onUpdateStatus={handleUpdateStatus}
      onUpdateTicket={handleUpdateTicket}
      onAddTicket={handleAddTicket}
      onAddContractor={handleAddContractor}
      onPaymentCreated={handlePaymentCreated}
      onAddRentPayment={handleAddRentPayment}
      onAddOwner={handleAddOwner}
      onAddInvoice={handleAddInvoice}
      onUpdateInvoice={handleUpdateInvoice}
      onAddLease={handleAddLease}
      onUpdateLease={handleUpdateLease}
      onAddDocument={handleAddDocument}
      onDeleteDocument={handleDeleteDocument}
      onSignOut={signOut}
    />
  );

  return null;
}
